import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Zap, ArrowRight, Loader2, Star, Lock, AlertTriangle, Crown, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANS = [
  {
    id: 'solelytax_monthly',
    name: 'Monthly',
    price: '$19',
    period: '/month',
    subtext: 'Billed monthly',
    tag: null,
    highlight: false,
    features: [
      'Unlimited transactions',
      'All reports & tax packages',
      'AI Advisors (IRS RAG-powered)',
      'Tax Leak Audit & PDF report',
      'CPA-ready exports',
      'Priority support',
    ],
  },
  {
    id: 'solelytax_yearly',
    name: 'Yearly — Founding Member',
    price: '$149',
    period: '/year',
    subtext: '$12.42/mo · Save $79/yr',
    tag: 'BEST VALUE',
    highlight: true,
    is_founding: true,
    foundingNote: 'Founding rate — available until 100 paid members',
    savingsBadge: 'Save $79',
    features: [
      'Everything in Monthly',
      'Save $79/yr vs monthly billing',
      'Founding member badge',
      'Early access to new features',
      'Locked-in founding rate forever',
    ],
  },
];

export default function Pricing() {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('return_to') === 'strategy-advisor' ? 'strategy-advisor' : null;
  const [paymentMode, setPaymentMode] = useState(null);
  useEffect(() => {
    axios.get(`${API}/subscription/payment-mode`, { withCredentials: true }).then(r => setPaymentMode(r.data)).catch(() => {});
  }, []);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [foundingStatus, setFoundingStatus] = useState(null);

  // Detect trial-expired redirect context
  const isTrialExpired = location.state?.plan_type === 'trial_expired'
    || location.state?.plan_type === 'data_deleted'
    || subscriptionStatus?.plan_type === 'trial_expired';
  const daysUntilDeletion = location.state?.days_until_deletion ?? subscriptionStatus?.days_until_deletion;

  useEffect(() => {
    if (user) checkSubscriptionStatus();
    // Public endpoint — no auth needed
    const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
    axios.get(`${API_BASE}/subscription/founding-status`)
      .then(r => setFoundingStatus(r.data))
      .catch(() => {});
  }, [user]);

  // Poll after Stripe redirect
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId && user) pollPaymentStatus(sessionId);
  }, [searchParams, user]);

  const checkSubscriptionStatus = async () => {
    try {
      const res = await axios.get(`${API}/subscription/status`, {
        headers: getAuthHeaders(), withCredentials: true,
      });
      setSubscriptionStatus(res.data);
      if (res.data.plan_type === 'paid') {
        toast.success('You already have an active subscription!');
        navigate(returnTo ? `/${returnTo}` : '/dashboard');
      }
    } catch {}
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 10) {
      toast.error('Payment verification timed out. Check your email for confirmation.');
      return;
    }
    setCheckingStatus(true);
    try {
      // ── Commandment VI: GET /checkout/status is read-only ──
      const res = await axios.get(`${API}/subscription/checkout/status/${sessionId}`, {
        headers: getAuthHeaders(), withCredentials: true,
      });
      if (res.data.payment_status === 'paid') {
        // Trigger the writer endpoint once to persist subscription + payment event
        await axios.post(`${API}/subscription/confirm`, { session_id: sessionId }, {
          headers: getAuthHeaders(), withCredentials: true,
        }).catch(() => { /* idempotent — ignore if already confirmed */ });
        if (returnTo) {
          navigate(`/${returnTo}?welcome=1`);
        } else {
          toast.success('Payment successful! Welcome to SolelyTax Pro.');
          navigate('/intake');
        }
        return;
      } else if (res.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setCheckingStatus(false);
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch {
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    }
  };

  const handleSubscribe = async (planId) => {
    if (!user) {
      toast.error('Please create an account first.');
      navigate('/register');
      return;
    }
    setLoadingPlan(planId);
    try {
      const res = await axios.post(
        `${API}/subscription/checkout`,
        { plan_id: planId, ...(returnTo ? { return_to: returnTo } : {}) },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      window.location.href = res.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout. Please try again.');
      setLoadingPlan(null);
    }
  };

  if (checkingStatus) {
    return (
      <div className="min-h-screen bg-[#0A0E17] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-emerald-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Verifying Payment...</h2>
          <p className="text-slate-400">Please wait while we confirm your subscription.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0E17]">
      {/* Header */}
      {paymentMode?.test_mode && (
        <div className="bg-amber-400 px-4 py-2 text-center text-sm font-semibold text-stone-950" data-testid="pricing-test-mode-banner">
          TEST MODE: no real charges. Use card {paymentMode.test_card}, any future expiry date and any CVC.
        </div>
      )}
      <div className="max-w-4xl mx-auto px-4 pt-16 pb-10 text-center">
        {returnTo && (
          <div className="mb-8 mx-auto max-w-xl rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-left text-sm text-emerald-100" data-testid="pricing-return-banner">
            <span className="font-semibold text-emerald-300">Unlock your Strategy Advisor report.</span> Pick any plan below and you'll go straight back to your interview after checkout.
          </div>
        )}
        {/* Trial-expired banner */}
        {isTrialExpired && (
          <div className={`mb-8 mx-auto max-w-xl rounded-xl p-4 border flex items-start gap-3 text-left ${
            daysUntilDeletion <= 10
              ? 'bg-red-950/40 border-red-700/50'
              : 'bg-amber-950/40 border-amber-700/50'
          }`} data-testid="trial-expired-banner">
            <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${daysUntilDeletion <= 10 ? 'text-red-400' : 'text-amber-400'}`} />
            <div>
              <p className={`font-bold ${daysUntilDeletion <= 10 ? 'text-red-300' : 'text-amber-300'}`}>
                Your 30-day free trial has ended
              </p>
              {daysUntilDeletion > 0 && (
                <p className="text-slate-400 text-sm mt-0.5">
                  Subscribe within <strong className={daysUntilDeletion <= 10 ? 'text-red-300' : 'text-amber-300'}>{daysUntilDeletion} days</strong> to preserve your data — or it will be permanently deleted.
                </p>
              )}
              {daysUntilDeletion === 0 && (
                <p className="text-red-300 text-sm mt-0.5 font-medium">Your data has been deleted. Subscribe to start fresh.</p>
              )}
            </div>
          </div>
        )}

        <Badge className="mb-4 bg-emerald-500/20 text-emerald-300 border-emerald-500/30">
          <Zap className="w-3 h-3 mr-1" />
          Tax Optimization OS
        </Badge>
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight">
          {returnTo ? 'Choose Your Plan' : isTrialExpired ? 'Keep Your Data — Subscribe Now' : 'Start Your 30-Day Free Trial'}
        </h1>
        <p className="text-lg text-slate-400 max-w-2xl mx-auto">
          {isTrialExpired
            ? 'Choose a plan to restore access and lock in the founding member rate before we hit 100 paid members.'
            : returnTo
            ? 'Every paid plan includes Strategy Advisor, plus everything you already use in SolelyTax.'
            : 'No credit card required. Full access for 30 days. Then choose the plan that works for you.'}
        </p>
      </div>

      {/* Plan Cards */}
      <div className="max-w-4xl mx-auto px-4 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
          {PLANS.map((plan) => (
            <div
              key={plan.id}
              className={`relative rounded-2xl border p-8 flex flex-col ${
                plan.highlight
                  ? 'bg-slate-900/80 border-emerald-500/60 shadow-[0_0_40px_-10px_rgba(16,185,129,0.25)]'
                  : 'bg-slate-900/40 border-slate-700/50'
              }`}
              data-testid={`plan-card-${plan.id}`}
            >
              {/* Tag */}
              {plan.tag && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                  <span className="bg-emerald-500 text-white text-xs font-bold px-4 py-1 rounded-full">
                    {plan.tag}
                  </span>
                </div>
              )}

              {/* Plan name */}
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-2">
                  <h2 className="text-xl font-bold text-white">{plan.name}</h2>
                  {plan.highlight && <Crown className="w-4 h-4 text-amber-400" />}
                </div>
                {plan.savingsBadge && (
                  <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 text-xs mb-2">
                    {plan.savingsBadge}
                  </Badge>
                )}
                {plan.foundingNote && (
                  <p className="text-xs text-emerald-400/80">{plan.foundingNote}</p>
                )}
              </div>

              {/* Price */}
              <div className="mb-6">
                <div className="flex items-end gap-1">
                  <span className="text-5xl font-bold text-white tracking-tight">{plan.price}</span>
                  <span className="text-slate-400 mb-1">{plan.period}</span>
                </div>
                <p className="text-slate-500 text-sm mt-1">{plan.subtext}</p>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-8 flex-1">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-sm text-slate-300">
                    <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>

              {/* Founding ticker — only on yearly card */}
              {plan.is_founding && foundingStatus && (
                <div className="mb-5 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl" data-testid="pricing-founding-ticker">
                  <div className="flex items-center justify-between text-xs mb-2">
                    <span className="flex items-center gap-1.5 text-amber-400 font-medium">
                      <Users className="w-3 h-3" />
                      {foundingStatus.used} / 100 founding spots claimed
                    </span>
                    <span className={`font-semibold ${foundingStatus.remaining <= 20 ? 'text-red-400' : 'text-amber-400'}`}>
                      {foundingStatus.remaining} left
                    </span>
                  </div>
                  <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-amber-500 to-orange-400 rounded-full transition-all"
                      style={{ width: `${Math.max(2, foundingStatus.percent_claimed)}%` }}
                    />
                  </div>
                </div>
              )}

              {/* CTA */}
              <Button
                className={`w-full py-6 text-base font-semibold rounded-xl ${
                  plan.highlight
                    ? 'bg-emerald-500 hover:bg-emerald-400 text-slate-950 shadow-lg shadow-emerald-500/20'
                    : 'bg-slate-700 hover:bg-slate-600 text-white'
                }`}
                onClick={() => handleSubscribe(plan.id)}
                disabled={loadingPlan !== null}
                data-testid={`subscribe-btn-${plan.id}`}
              >
                {loadingPlan === plan.id
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Redirecting to Stripe...</>
                  : <>{isTrialExpired ? 'Subscribe Now' : 'Get Started'} <ArrowRight className="w-4 h-4 ml-2" /></>
                }
              </Button>
            </div>
          ))}
        </div>

        {/* Trust row */}
        <div className="flex flex-wrap items-center justify-center gap-6 mt-10 text-xs text-slate-500 uppercase tracking-widest">
          <span className="flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-500" />Cancel anytime</span>
          <span className="flex items-center gap-1.5"><Lock className="w-3 h-3 text-emerald-500" />256-bit SSL</span>
          <span className="flex items-center gap-1.5"><Star className="w-3 h-3 text-emerald-500" />30-day money-back guarantee</span>
        </div>

        {/* Already have account */}
        {!user && (
          <p className="text-center text-slate-500 text-sm mt-8">
            Already have an account?{' '}
            <button onClick={() => navigate('/login')} className="text-emerald-400 hover:text-emerald-300 font-medium">
              Sign in
            </button>
          </p>
        )}
      </div>
    </div>
  );
}
